import type { MessageInstance } from "antd/es/message/interface";
import type { BillOut, TableReservationNotice } from "../../../../../api/bills";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { setSelectedBillId } from "../../../../../store";
import { useConfiguration } from "../../../../../hooks/useConfiguration";
import { useOrderingFloor } from "./useOrderingFloor";
import { useOpenSchemaTable } from "./useOpenSchemaTable";
import { useSchemaSelection } from "./useSchemaSelection";
import { useSchemaWaiterFilter } from "./useSchemaWaiterFilter";

/**
 * Режимът „Схема" за `Ordering` в едно: полето „Сервитьор"
 * (`useSchemaWaiterFilter` — кои сметки се показват), залата
 * (`useOrderingFloor`), изборът по маса (`useSchemaSelection`) и „Нова маса"
 * за свободна маса (`useOpenSchemaTable`). Вика се в `Ordering`, за да оцелее при отиване в
 * менюто и обратно; при `schemaMode` false нищо не се чете.
 */
export const useOrderingSchema = ({
  schemaMode,
  isTablesView,
  tableBills,
  sortedBills,
  closingBillIds,
  selectedBill,
  askBeforeNewTable,
  confirmNotice,
  message,
  onSelectBill,
  onOpenBill,
  onOpenNewTable,
}: {
  schemaMode: boolean;
  isTablesView: boolean;
  tableBills: BillOut[];
  sortedBills: BillOut[];
  closingBillIds: ReadonlySet<number>;
  selectedBill: BillOut | null;
  askBeforeNewTable: () => Promise<boolean>;
  confirmNotice: (name: string, notice: TableReservationNotice) => Promise<boolean>;
  message: MessageInstance;
  /** Клик по маса — пресни сметки + избор (null — свободна маса или нищо). */
  onSelectBill: (billId: number | null) => void;
  /** „Добави" за конкретна сметка (двоен клик). */
  onOpenBill: (billId: number) => void;
  /** Влиза в менюто за нова маса с това име (`handleNewTableSubmit`). */
  onOpenNewTable: (name: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canOpenBill = permissions.some((permission) => permission.name === "open_bill");

  // Първо полето „Сервитьор": залата и изборът виждат само показаните сметки.
  // Обща номерация (настройка на ресторанта): чуждите маси са бледи, но в
  // залата; лична — залата е само на показания сервитьор.
  const sharedNumbering = useConfiguration().configuration.orderingSharedNumbering;
  const waiterFilter = useSchemaWaiterFilter({
    enabled: schemaMode,
    tableBills,
    sortedBills,
    selectedBill,
    sharedNumbering,
    isTablesView,
    onClearSelection: () => dispatch(setSelectedBillId(null)),
  });
  const floor = useOrderingFloor({
    enabled: schemaMode,
    tableBills: waiterFilter.tableBills,
    allTableBills: tableBills,
    sortedBills: waiterFilter.sortedBills,
    closingBillIds,
    sharedNumbering,
    focusWaiterId: waiterFilter.focusWaiterId,
  });
  // Последният избор печели: нов избор в схемата (клик, двоен клик, празният
  // под, картите в лентите), изход от схемата или меню, отворено по друг път
  // (`active` — схемата вече не е на екрана) обезсилва висящото „Нова маса" —
  // късният отговор за предишната маса не вкарва в менюто ѝ.
  const { openSchemaTable, cancelPendingOpen } = useOpenSchemaTable({
    active: schemaMode && isTablesView,
    askBeforeNewTable,
    confirmNotice,
    message,
    onOpen: onOpenNewTable,
  });
  const selection = useSchemaSelection({
    active: schemaMode && isTablesView,
    floor,
    sortedBills: waiterFilter.sortedBills,
    closingBillIds,
    selectedBill,
    onSelectBill: (billId) => {
      cancelPendingOpen();
      onSelectBill(billId);
    },
    onSyncBill: (billId) => dispatch(setSelectedBillId(billId)),
    onOpenBill: (billId) => {
      cancelPendingOpen();
      onOpenBill(billId);
    },
    onOpenNamedTable: (name) => void openSchemaTable(name),
    canOpenBill,
    waiterChoice: waiterFilter.choice,
    focusWaiterId: waiterFilter.focusWaiterId,
    sharedNumbering,
  });

  // „Нова маса": за избраната свободна маса — направо с името ѝ. Без такава
  // в схемата бутонът е заключен (`Ordering`); в списъка — обичайният път.
  const freeTableName = schemaMode ? selection.freeTable?.name || null : null;

  return {
    floor,
    selection,
    canOpenBill,
    /** „Нова маса" за избраната свободна маса; null — в схемата бутонът е
     *  заключен, в списъка — обичайният път. */
    openFreeTable: freeTableName !== null ? () => void openSchemaTable(freeTableName) : null,
  };
};
